import React from 'react';
import { CheckCircle, AlertTriangle, Clock, Plus, Play } from 'lucide-react';
interface ClaimsValidationCardProps {
  onAddClaim: () => void;
}
export function ClaimsValidationCard({
  onAddClaim
}: ClaimsValidationCardProps) {
  const claims = [{
    claim: 'Supports immune system health*',
    status: 'validated',
    lastValidated: '2 days ago',
    details: 'Vitamin C and D3 content verified against FDA guidelines'
  }, {
    claim: 'High potency formula',
    status: 'issues',
    lastValidated: '2 hours ago',
    details: 'B-Complex potency exceeds typical "high potency" thresholds - may need adjustment'
  }, {
    claim: 'Third-party tested for purity',
    status: 'validated',
    lastValidated: '1 week ago',
    details: 'COA certificates verified for all active ingredients'
  }, {
    claim: 'Essential vitamins and minerals',
    status: 'pending',
    lastValidated: 'Never',
    details: 'Awaiting review - need to verify essential nutrient definitions'
  }, {
    claim: 'Supports energy metabolism*',
    status: 'validated',
    lastValidated: '3 days ago',
    details: 'B-vitamin complex supports energy metabolism - FDA compliant'
  }];
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'validated':
        return <CheckCircle className="w-4 h-4 text-green-600" data-id="element-153" />;
      case 'issues':
        return <AlertTriangle className="w-4 h-4 text-red-600" data-id="element-154" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" data-id="element-155" />;
      default:
        return null;
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'validated':
        return 'bg-green-100 text-green-800';
      case 'issues':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const validatedCount = claims.filter(c => c.status === 'validated').length;
  const issuesCount = claims.filter(c => c.status === 'issues').length;
  const pendingCount = claims.filter(c => c.status === 'pending').length;
  return <div className="bg-white rounded-lg border" data-id="element-156">
      <div className="p-6 border-b border-gray-200" data-id="element-157">
        <div className="flex items-center justify-between" data-id="element-158">
          <div data-id="element-159">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-160">
              Claims Validation
            </h2>
            <div className="flex items-center space-x-4 mt-1 text-sm" data-id="element-161">
              <span className="text-green-600" data-id="element-162">{validatedCount} validated</span>
              <span className="text-red-600" data-id="element-163">{issuesCount} issues</span>
              <span className="text-yellow-600" data-id="element-164">{pendingCount} pending</span>
            </div>
          </div>
          <div className="flex items-center space-x-3" data-id="element-165">
            <button onClick={onAddClaim} className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg" data-id="element-166">
              <Plus className="w-4 h-4" data-id="element-167" />
              <span data-id="element-168">Add Claim</span>
            </button>
            
          </div>
        </div>
      </div>
      <div className="divide-y divide-gray-200" data-id="element-169">
        {claims.map((item, index) => <div key={index} className="p-6" data-id="element-170">
            <div className="flex items-start justify-between" data-id="element-171">
              <div className="flex-1" data-id="element-172">
                <div className="flex items-center space-x-3 mb-2" data-id="element-173">
                  {getStatusIcon(item.status)}
                  <span className="font-medium text-gray-900" data-id="element-174">
                    {item.claim}
                  </span>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.status)}`} data-id="element-175">
                    {item.status === 'validated' ? 'Validated' : item.status === 'issues' ? 'Issues' : 'Pending'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2" data-id="element-176">{item.details}</p>
                <p className="text-xs text-gray-500" data-id="element-177">
                  Last validated: {item.lastValidated}
                </p>
              </div>
              <button className="text-purple-600 hover:text-purple-700 text-sm font-medium ml-4" data-id="element-178">
                Review
              </button>
            </div>
          </div>)}
      </div>
      <div className="p-6 border-t border-gray-200 bg-gray-50" data-id="element-179">
        <div className="flex items-center justify-between text-sm" data-id="element-180">
          <div className="flex items-center space-x-2" data-id="element-181">
            <CheckCircle className="w-4 h-4 text-green-600" data-id="element-182" />
            <span className="text-gray-700" data-id="element-183">
              FDA Disclaimer: *This statement has not been evaluated by the FDA
            </span>
          </div>
          <span className="text-green-600 font-medium" data-id="element-184">Compliant</span>
        </div>
      </div>
    </div>;
}